import {
  ArticleOutlined,
  CheckCircleOutlineOutlined,
  EditNoteOutlined,
  RemoveCircleOutline,
  SimCardAlertOutlined,
} from '@mui/icons-material'
import { Box, styled, useTheme } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useGetFormSchemaReportTemplateCounts } from 'src/api/report-template'
import { ReportTemplateStatus } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { ReportTemplateSummaryCard } from './ReportTemplateSummaryCard'

export interface ReportTemplateSummaryCardsProps {
  customerId: EntityId
  selectedStatus: ReportTemplateStatus | null
  onStatusChange: (status: ReportTemplateStatus | null) => void
}

interface CardConfig {
  status: ReportTemplateStatus | null
  labelKey: string
  colorKey: MUIThemeColors
  icon: React.ReactNode
}

export const ReportTemplateSummaryCards: React.FC<ReportTemplateSummaryCardsProps> = ({
  customerId,
  selectedStatus,
  onStatusChange,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation('dynamic-report')
  const { data: counts } = useGetFormSchemaReportTemplateCounts(customerId)

  const CARD_CONFIGS: CardConfig[] = [
    {
      status: null,
      labelKey: 'total-templates',
      colorKey: MUIThemeColors.primaryLight,
      icon: <ArticleOutlined fontSize="small" />,
    },
    {
      status: ReportTemplateStatus.Active,
      labelKey: 'active',
      colorKey: MUIThemeColors.successLight,
      icon: <CheckCircleOutlineOutlined fontSize="small" />,
    },
    {
      status: ReportTemplateStatus.Inactive,
      labelKey: 'inactive',
      colorKey: MUIThemeColors.secondaryMain,
      icon: <RemoveCircleOutline fontSize="small" />,
    },
    {
      status: ReportTemplateStatus.Draft,
      labelKey: 'draft',
      colorKey: MUIThemeColors.infoMain,
      icon: <EditNoteOutlined fontSize="small" />,
    },
    {
      status: ReportTemplateStatus.Warning,
      labelKey: 'warning',
      colorKey: MUIThemeColors.warningLight,
      icon: <SimCardAlertOutlined fontSize="small" />,
    },
  ]

  const getCount = (status: ReportTemplateStatus | null): number => {
    if (!counts) return 0
    if (status === null) return counts.totalTemplates
    if (status === ReportTemplateStatus.Active) return counts.activeTemplates
    if (status === ReportTemplateStatus.Inactive) return counts.inactiveTemplates
    if (status === ReportTemplateStatus.Draft) return counts.draftTemplates
    if (status === ReportTemplateStatus.Warning) return counts.warningTemplates
    return 0
  }

  const getColorFromTheme = (colorKey: MUIThemeColors): string => {
    return getThemeColor(theme, colorKey)
  }

  return (
    <CardsContainer>
      {CARD_CONFIGS.map(config => {
        const color = getColorFromTheme(config.colorKey)
        return (
          <ReportTemplateSummaryCard
            key={config.status ?? 'total'}
            label={translate(config.labelKey)}
            count={getCount(config.status)}
            icon={config.icon}
            accentColor={color}
            dotColor={color}
            isSelected={selectedStatus === config.status}
            hasActiveSelection={selectedStatus !== null}
            onClick={() => onStatusChange(selectedStatus === config.status ? null : config.status)}
          />
        )
      })}
    </CardsContainer>
  )
}

const CardsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  width: '100%',
}))
